import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './useAuth';

export const useSubscription = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscriptionTiers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_tiers')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly', { ascending: true });

      if (error) {
        console.error('Error fetching subscription tiers:', error);
        // Don't throw error, just log it - component will use fallback
      } else {
        setSubscriptionTiers(data || []);
      }
    } catch (err) {
      console.error('Error fetching subscription tiers:', err);
      // Don't set error state, just log it - component will use fallback
    }
  }, []);

  const fetchUserSubscription = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      
      // Use the database function to get user's subscription tier
      const { data, error } = await supabase
        .rpc('get_user_subscription_tier', { user_uuid: user.id });

      if (error) throw error;
      
      if (data && data.length > 0) {
        setSubscription(data[0]);
      } else {
        // Default to free tier if no subscription found
        setSubscription({
          tier_name: 'free',
          tier_display_name: 'Free',
          max_project_groups: 2,
          max_projects_per_group: 3,
          max_tasks_per_project: 25,
          features: {
            priority_support: false,
            advanced_analytics: false,
            team_collaboration: false
          },
          status: 'active',
          current_period_end: null
        });
      }
    } catch (err) {
      console.error('Error fetching user subscription:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const checkFeatureAccess = useCallback(async (featureName) => {
    if (!user?.id) return false;

    try {
      const { data, error } = await supabase
        .rpc('user_has_feature_access', { 
          user_uuid: user.id, 
          feature_name: featureName 
        });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error checking feature access:', err);
      return false;
    }
  }, [user?.id]);

  const hasFeature = useCallback((featureName) => {
    if (!subscription?.features) return false;
    return subscription.features[featureName] === true;
  }, [subscription]);

  const isWithinLimits = useCallback((type, currentCount) => {
    if (!subscription) return false;
    
    const limits = {
      project_groups: subscription.max_project_groups,
      projects_per_group: subscription.max_projects_per_group,
      tasks_per_project: subscription.max_tasks_per_project
    };

    const limit = limits[type];
    if (limit === -1) return true; // Unlimited
    return currentCount < limit;
  }, [subscription]);

  const getLimitInfo = useCallback((type) => {
    if (!subscription) return { limit: 0, isUnlimited: false };
    
    const limits = {
      project_groups: subscription.max_project_groups,
      projects_per_group: subscription.max_projects_per_group,
      tasks_per_project: subscription.max_tasks_per_project
    };

    const limit = limits[type];
    return {
      limit: limit === -1 ? 0 : limit,
      isUnlimited: limit === -1
    };
  }, [subscription]);

  const upgradeSubscription = useCallback(async (tierName, billingCycle = 'monthly') => {
    if (!user?.id) throw new Error('User not authenticated');

    try {
      // This is a mock implementation - in real app, this would integrate with Stripe
      console.log('Mock upgrade to:', tierName, billingCycle);
      
      // For now, just simulate the upgrade
      const targetTier = subscriptionTiers.find(tier => tier.name === tierName);
      if (!targetTier) throw new Error('Invalid subscription tier');

      // In real implementation, you would:
      // 1. Create Stripe checkout session
      // 2. Handle payment success webhook
      // 3. Update user subscription in database
      
      // Mock success response
      return {
        success: true,
        message: 'Subscription upgrade initiated',
        checkoutUrl: `https://mock-stripe-checkout.com/session_${Date.now()}`
      };
    } catch (err) {
      console.error('Error upgrading subscription:', err);
      throw err;
    }
  }, [user?.id, subscriptionTiers]);

  const cancelSubscription = useCallback(async () => {
    if (!user?.id) throw new Error('User not authenticated');

    try {
      // Mock cancellation - in real app, this would cancel Stripe subscription
      console.log('Mock cancellation');
      
      // Update local state to reflect cancellation
      setSubscription(prev => ({
        ...prev,
        status: 'canceled'
      }));

      return { success: true, message: 'Subscription canceled' };
    } catch (err) {
      console.error('Error canceling subscription:', err);
      throw err;
    }
  }, [user?.id]);

  useEffect(() => {
    const loadTiers = async () => {
      setLoading(true);
      await fetchSubscriptionTiers();
      setLoading(false);
    };
    loadTiers();
  }, [fetchSubscriptionTiers]);

  useEffect(() => {
    fetchUserSubscription();
  }, [fetchUserSubscription]);

  return {
    subscription,
    subscriptionTiers,
    loading,
    error,
    hasFeature,
    isWithinLimits,
    getLimitInfo,
    checkFeatureAccess,
    upgradeSubscription,
    cancelSubscription,
    refetchSubscription: fetchUserSubscription
  };
};
